import axios, { AxiosInstance } from 'axios';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const TOKEN_KEY = 'mt_token';
const USER_KEY = 'mt_user';

export type Role = 'SuperAdmin' | 'BusinessUnit' | 'Store' | 'Managers' | 'Employees';

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: Role;
};

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setSession(token: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== '/login') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    setSession(data.token, data.user);
    return data as { token: string; user: AuthUser };
  },
  me: async () => {
    const { data } = await api.get('/auth/me');
    return data;
  },
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearSession();
    }
  },
};

export const orgApi = {
  listBusinessUnits: () => api.get('/api/business-units').then((r) => r.data),
  createBusinessUnit: (body: any) => api.post('/api/business-units', body).then((r) => r.data),
  updateBusinessUnit: (id: string, body: any) => api.put(`/api/business-units/${id}`, body).then((r) => r.data),
  deleteBusinessUnit: (id: string) => api.delete(`/api/business-units/${id}`).then((r) => r.data),
  listStores: (businessUnitId?: string) =>
    api.get('/api/stores', { params: { businessUnitId } }).then((r) => r.data),
  createStore: (body: any) => api.post('/api/stores', body).then((r) => r.data),
  listManagers: () => api.get('/api/managers').then((r) => r.data),
  createManager: (body: any) => api.post('/api/managers', body).then((r) => r.data),
  listEmployees: (params?: { storeId?: string; createdBy?: string; managerId?: string }) =>
    api.get('/api/employees', { params }).then((r) => r.data),
  createEmployee: (body: any) => api.post('/api/employees', body).then((r) => r.data),
  updateEmployee: (id: string, body: any) => api.put(`/api/employees/${id}`, body).then((r) => r.data),
  deleteEmployee: (id: string) => api.delete(`/api/employees/${id}`).then((r) => r.data),
  myStores: () => api.get('/api/my-stores').then((r) => r.data),
};

export const coursesApi = {
  list: (params?: any) => api.get('/api/courses', { params }).then((r) => r.data),
  get: (id: string) => api.get(`/api/courses/${id}`).then((r) => r.data),
  create: (body: any) => api.post('/api/courses', body).then((r) => r.data),
  update: (id: string, body: any) => api.put(`/api/courses/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/courses/${id}`).then((r) => r.data),
  addQuestion: (courseId: string, body: any) =>
    api.post(`/api/courses/${courseId}/questions`, body).then((r) => r.data),
  listQuestions: (courseId: string) => api.get(`/api/courses/${courseId}/questions`).then((r) => r.data),
};

export const assignmentsApi = {
  list: (params?: any) => api.get('/api/assignments', { params }).then((r) => r.data),
  create: (body: any) => api.post('/api/assignments', body).then((r) => r.data),
  update: (id: string, body: any) => api.put(`/api/assignments/${id}`, body).then((r) => r.data),
};

export const resultsApi = {
  create: (body: any) => api.post('/api/results', body).then((r) => r.data),
  list: (params?: any) => api.get('/api/results', { params }).then((r) => r.data),
};

export const learningPathsApi = {
  list: (params?: any) => api.get('/api/learning-paths', { params }).then((r) => r.data),
  get: (id: string) => api.get(`/api/learning-paths/${id}`).then((r) => r.data),
  create: (body: any) => api.post('/api/learning-paths', body).then((r) => r.data),
  update: (id: string, body: any) => api.put(`/api/learning-paths/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/learning-paths/${id}`).then((r) => r.data),
  assign: (id: string, body: any) => api.post(`/api/learning-paths/${id}/assign`, body).then((r) => r.data),
  listAssignments: (params?: any) =>
    api.get('/api/learning-paths/assignments/list', { params }).then((r) => r.data),
};

export const mediaApi = {
  upload: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post('/api/media/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as { id: string; key: string; url: string; originalName: string };
  },
};

export const watiApi = {
  send: (body: any) => api.post('/api/wati/send', body).then((r) => r.data),
  listConversations: (params?: any) => api.get('/api/wati/conversations', { params }).then((r) => r.data),
  createConversation: (body: any) => api.post('/api/wati/conversations', body).then((r) => r.data),
  createMessage: (body: any) => api.post('/api/wati/messages', body).then((r) => r.data),
};

export const supportApi = {
  list: (params?: any) => api.get('/api/support', { params }).then((r) => r.data),
  create: (body: any) => api.post('/api/support', body).then((r) => r.data),
};

export const schedulingApi = {
  checkEligibility: (employeeId: string) =>
    api.post(`/api/scheduling/eligibility/${employeeId}`).then((r) => r.data),
};
